import { useCallback, useState } from 'react'
import { AppDialog, type AppDialogAction } from '../Common/AppDialog'
import { TextInput } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import { useWorkspace } from '../../context/useWorkspace'
import { claimAccountTransfer, mapAccountTransferError } from '../../services/accountTransferService'

export interface ClaimTransferCodeModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: (transferredCount: number) => void
}

export const ClaimTransferCodeModal = ({
  open,
  onClose,
  onSuccess,
}: ClaimTransferCodeModalProps) => {
  const { t } = useNls()
  const { refreshWorkspaces } = useWorkspace()
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refusalWorkspaces, setRefusalWorkspaces] = useState<string[] | null>(null)
  const [transferredCount, setTransferredCount] = useState<number | null>(null)

  const handleDismiss = useCallback(() => {
    setCode('')
    setLoading(false)
    setError(null)
    setRefusalWorkspaces(null)
    setTransferredCount(null)
    onClose()
  }, [onClose])

  const handleClaim = useCallback(async () => {
    const trimmed = code.trim()
    if (!trimmed) return
    setLoading(true)
    setError(null)
    setRefusalWorkspaces(null)
    try {
      const res = await claimAccountTransfer({ code: trimmed })
      setTransferredCount(res.workspacesTransferred)
      await refreshWorkspaces?.()
      onSuccess?.(res.workspacesTransferred)
    } catch (err) {
      const mapped = mapAccountTransferError(err)
      setError(t(mapped.nlsKey))
      if (mapped.details?.workspaces && mapped.details.workspaces.length > 0) {
        setRefusalWorkspaces(mapped.details.workspaces.map((w) => w.name))
      }
    } finally {
      setLoading(false)
    }
  }, [code, onSuccess, refreshWorkspaces, t])

  const dialogActions: AppDialogAction[] =
    transferredCount !== null
      ? [
          {
            label: 'Done',
            variant: 'primary',
            onClick: onClose,
          },
        ]
      : [
          {
            label: 'Cancel',
            variant: 'ghost',
            onClick: onClose,
            disabled: loading,
          },
          {
            label: t('NLSAccountTransferClaimButton'),
            variant: 'primary',
            onClick: handleClaim,
            loading,
            disabled: !code.trim() || loading,
          },
        ]

  return (
    <AppDialog
      open={open}
      onDismiss={handleDismiss}
      title={t('NLSAccountTransferClaimDialogTitle')}
      message={
        <div className="claimTransferCodeModalBody flex flex-col gap-4 text-sm">
          {transferredCount !== null ? (
            <div className="p-4 rounded-lg bg-[var(--success-soft)] border border-[var(--border-subtle)] text-[var(--success)]">
              <p className="font-semibold">
                {t('NLSAccountTransferSuccessMessage', { count: transferredCount })}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs text-[var(--muted)]">
                  {t('NLSAccountTransferClaimDialogTitle')}
                </span>
                <TextInput
                  block
                  value={code}
                  onChange={setCode}
                  placeholder={t('NLSAccountTransferCodePlaceholder')}
                  disabled={loading}
                  autoFocus
                />
              </label>

              {error && (
                <div className="p-3 rounded-lg bg-[var(--danger-soft)] border border-[var(--border-subtle)] text-[var(--danger)] flex flex-col gap-2">
                  <p className="text-xs font-semibold">{error}</p>
                  {refusalWorkspaces && refusalWorkspaces.length > 0 && (
                    <ul className="list-disc list-inside text-xs flex flex-col gap-1 pl-1">
                      {refusalWorkspaces.map((name, i) => (
                        <li key={i}>{name}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      }
      actions={dialogActions}
    />
  )
}
